/* RON ACCESS: same-origin Shopify app-proxy client. No customer IDs or secrets. */
(() => {
  'use strict';
  if (window.RonAccessAPI) return;
  class AccessError extends Error {
    constructor(code, status = 0, fields = null) {
      super(code);
      this.name = 'AccessError';
      this.code = code;
      this.status = status;
      this.fields = fields;
    }
  }
  const withPreview = (value) => {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) throw new AccessError('INVALID_RETURN_URL');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search + url.hash;
  };
  const getLoginUrl = (returnTo, email) => {
    const params = new URLSearchParams({ return_to: withPreview(returnTo) });
    if (email && email.length <= 254) params.set('login_hint', email);
    return '/customer_authentication/login?' + params.toString();
  };
  const create = ({ basePath = '/apps/ron-access' } = {}) => {
    if (!/^\/apps\/[a-zA-Z0-9_-]+\/?$/.test(basePath)) throw new AccessError('INVALID_API_PATH');
    const base = basePath.replace(/\/$/, '');
    let session = null;
    let sessionPromise = null;
    async function request(path, { method = 'GET', body, csrfToken, idempotencyKey } = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const headers = { Accept: 'application/json' };
        if (body !== undefined) headers['Content-Type'] = 'application/json';
        if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
        if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
        const response = await fetch(base + path, {
          method, headers, body: body === undefined ? undefined : JSON.stringify(body),
          credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal
        });
        if (!response.headers.get('content-type')?.includes('application/json')) {
          throw new AccessError('SERVICE_UNAVAILABLE', response.status);
        }
        const payload = await response.json();
        if (!response.ok) throw new AccessError(payload?.error?.code || 'SAVE_FAILED', response.status, payload?.error?.fields);
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new AccessError('INVALID_RESPONSE', response.status);
        return payload;
      } catch (error) {
        if (error instanceof AccessError) throw error;
        throw new AccessError(error.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK_ERROR');
      } finally { clearTimeout(timeout); }
    }
    async function getSession() {
      if (session && Date.parse(session.csrfExpiresAt) > Date.now() + 30000) return session;
      if (!sessionPromise) sessionPromise = request('/session').then(value => {
        if (typeof value.csrfToken !== 'string' || !value.csrfToken) throw new AccessError('INVALID_RESPONSE');
        session = value;
        return value;
      }).finally(() => { sessionPromise = null; });
      return sessionPromise;
    }
    function memberResponse(value, allowNull = false) {
      if (allowNull && value.member === null) return value;
      if (!value.member || value.member.status !== 'active' || !value.member.profile || !value.member.consents || !Number.isInteger(value.member.revision)) {
        throw new AccessError('INVALID_RESPONSE');
      }
      return value;
    }
    async function write(path, method, body, options = {}) {
      const idempotencyKey = options.idempotencyKey || crypto.randomUUID();
      const auth = await getSession();
      try {
        return memberResponse(await request(path, { method, body, idempotencyKey, csrfToken: auth.csrfToken }));
      } catch (error) {
        // A rejected CSRF token did not write anything. Refresh once using the same operation key.
        if (error.status !== 403 || !['CSRF_INVALID', 'CSRF_EXPIRED', 'INVALID_CSRF'].includes(error.code)) throw error;
        session = null;
        const renewed = await getSession();
        return memberResponse(await request(path, { method, body, idempotencyKey, csrfToken: renewed.csrfToken }));
      }
    }
    return Object.freeze({
      getMember: async () => memberResponse(await request('/member'), true),
      enroll: (payload, options) => write('/enroll', 'POST', payload, options),
      updatePreferences: (payload, options) => write('/preferences', 'PATCH', payload, options),
      resetSession: () => { session = null; }
    });
  };
  window.RonAccessAPI = Object.freeze({ create, getLoginUrl, withPreview, AccessError });
})();
